import { DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import {
  EAboutUsPageTabs,
  ETableTitleAboutUsManagementCoreValue,
  PLACEHOLDER_IMAGE_AVATAR,
} from '@configs'
import { IPhotoMedia } from '@interfaces'
import {
  RootState,
  addNewCoreValues,
  deleteCoreValues,
  getListCoreValues,
  updateCoreValues,
  useAppDispatch,
  useAppSelector,
} from '@redux'
import { Card, Skeleton, Tooltip, message } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, SharedTable } from 'src/common'
import {
  ICommonContentDetail,
  ICoreValueFormData,
} from 'src/interfaces/aboutus-management'
import ConfirmDeleteModal from '../staff-and-role-management/ConfirmDeleteModal'
import { CoreValueDetailModal } from './core-value/CoreValueDetailModal'

interface ITransactionHistoryTabProps {
  activeTab?: string
}

const CoreValueTab = ({ activeTab }: ITransactionHistoryTabProps) => {
  const [showModal, setShowModal] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [selectedAboutUs, setSelectedAboutUs] = useState<
    ICommonContentDetail | undefined | null
  >()
  const [reset, setReset] = useState(false)
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false)
  const [selectedAboutUsId, setSelectedAboutUsId] = useState<number | null>(
    null
  )
  const [noDefault, setNoDefault] = useState(0)

  const { t } = useTranslation(['error', 'common'])

  const dispatch = useAppDispatch()

  const getListCoreValuesLoading = useAppSelector(
    (state: RootState) =>
      state.aboutUsManagement.loadings?.getListCoreValuesLoading
  )

  const listCoreValues = useAppSelector(
    (state: RootState) => state.aboutUsManagement?.coreValues
  )

  const onClose = () => {
    setShowModal(false)
  }

  const onOpen = () => {
    setSelectedAboutUs(null)
    setShowModal(true)
  }

  const onCloseConfirmModal = () => {
    setOpenConfirmDeleteModal(false)
  }

  const columns = [
    {
      title: () => {
        return (
          <div className="flex items-center justify-center w-full">
            <p className="m-0">{ETableTitleAboutUsManagementCoreValue.NO}</p>
          </div>
        )
      },
      dataIndex: 'no',
      align: 'center',
      key: 'no',
      width: '7%',
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
      ellipsis: true,
    },
    {
      title: ETableTitleAboutUsManagementCoreValue.TITLE,
      dataIndex: 'title',
      key: 'title',
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
      ellipsis: true,
      width: '20%',
    },
    {
      title: ETableTitleAboutUsManagementCoreValue.CONTENT,
      dataIndex: 'content',
      key: 'content',
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
      ellipsis: true,
    },
    {
      title: ETableTitleAboutUsManagementCoreValue.IMAGE,
      align: 'center',
      dataIndex: 'media',
      key: 'media',
      render: (record: IPhotoMedia) => (
        <div className="flex items-center justify-center w-full">
          <img
            src={record?.url ?? PLACEHOLDER_IMAGE_AVATAR}
            alt={record?.url}
            className="object-cover w-14 h-14"
          />
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div className="flex items-center justify-center w-full">
            <p className="m-0">
              {ETableTitleAboutUsManagementCoreValue.ACTION}
            </p>
          </div>
        )
      },
      key: 'action',
      dataIndex: 'action',
      width: '15%',
      render: (record: ICommonContentDetail) => (
        <div className="flex space-x-4 items-center justify-center w-full">
          <Tooltip title={t('common:view_detail')}>
            <EyeOutlined
              className="text-lg font-light mr-2.5 cursor-pointer text-[#184f64]"
              onClick={() => {
                setSelectedAboutUs(record)
                setShowModal(true)
              }}
            />
          </Tooltip>
          <DeleteOutlined
            className={`text-lg font-light mr-2.5 cursor-pointer  hover:opacity-80 !text-red-600`}
            onClick={() => {
              setOpenConfirmDeleteModal(true)
              setSelectedAboutUsId(record.id)
            }}
          />
        </div>
      ),
      ellipsis: true,
    },
  ]

  const onDelete = async () => {
    //on delete about us
    try {
      if (selectedAboutUsId) {
        setIsModalLoading(true)
        const response = await dispatch(
          deleteCoreValues(selectedAboutUsId)
        ).unwrap()
        setShowModal(false)
        setReset((r) => !r)
        if (response?.success) {
          message.success(response.message)
          setIsModalLoading(false)
          setOpenConfirmDeleteModal(false)
          dispatch(getListCoreValues())
        }
      } else {
        setShowModal(false)
      }
    } catch (error: any) {
      message.error(error.message)
    }
  }

  const onSave = async (data: ICoreValueFormData) => {
    //on save about us
    try {
      if (!!selectedAboutUs) {
        const response = await dispatch(
          updateCoreValues({ params: data, itemId: selectedAboutUs.id })
        ).unwrap()
        setShowModal(false)
        setReset((r) => !r)
        if (response?.success) message.success(response.message)
      } else {
        const response = await dispatch(addNewCoreValues(data)).unwrap()
        setShowModal(false)
        setReset((r) => !r)
        if (response?.success) message.success(response.message)
      }
      dispatch(getListCoreValues())
    } catch (error: any) {
      message.error(error.message)
    }
    try {
    } catch (error) {}
  }

  useEffect(() => {
    if (activeTab === EAboutUsPageTabs.CORE_VALUES) {
      dispatch(getListCoreValues())
    }
  }, [reset, activeTab])

  return (
    <Card>
      <div
        style={{
          display: 'flex',
        }}
      >
        <div
          style={{
            flex: 1,
          }}
        />
        <Button
          htmlType="submit"
          type="primary"
          size="middle"
          className="submit__btn loginselected-btn"
          onClick={onOpen}
          style={{
            alignSelf: 'flex-end',
          }}
        >
          {'Add new'}
        </Button>
      </div>
      {getListCoreValuesLoading ? (
        <Skeleton
          paragraph={{ rows: 4 }}
          style={{
            marginTop: 10,
          }}
        />
      ) : (
        <SharedTable
          columns={columns as any}
          dataSource={listCoreValues?.map((item) => {
            return {
              ...item,
              key: item?.id,
              no: item?.no,
              title: item?.title ?? `--`,
              content: item?.content ?? `--`,
              media: item?.media ?? `--`,
              mediaDescription: item?.mediaDescription ?? `--`,
              record: item,
              action: item,
            }
          })}
          sticky
          hasIndexColumn={false}
        />
      )}
      {showModal && (
        <CoreValueDetailModal
          open={showModal}
          onClose={onClose}
          isLoading={isModalLoading}
          changeLoading={setIsModalLoading}
          onSave={onSave}
          item={selectedAboutUs}
          // noDefault={noDefault + 1 ?? 1}
          noDefault={listCoreValues.length + 1 || 1}
        />
      )}
      {openConfirmDeleteModal && (
        <ConfirmDeleteModal
          title="Delete core value"
          content="Are you sure to delete this core value?"
          open={openConfirmDeleteModal}
          isLoading={!!isModalLoading}
          onDelete={onDelete}
          onClose={onCloseConfirmModal}
        />
      )}
    </Card>
  )
}

export default CoreValueTab
